import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb, { getCategoryName } from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'yule-bagua');

  const categoryArticles = getArticlesByCategory(activeSlug);
  const currentCategory = categoryMap[activeSlug];

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '分类'} - 吃瓜爆料`,
    description: currentCategory?.description || '吃瓜爆料分类页面',
    url: `https://www.chiguabaoliao.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '吃瓜爆料',
      url: 'https://www.chiguabaoliao.com',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.chiguabaoliao.com' },
      { '@type': 'ListItem', position: 2, name: currentCategory?.name || '分类', item: `https://www.chiguabaoliao.com/category/${activeSlug}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory?.name || '分类'} - 吃瓜爆料`}
        description={currentCategory?.description || '吃瓜爆料分类页面'}
        keywords={`吃瓜爆料,${currentCategory?.name || ''},吃瓜爆料网`}
        canonicalUrl={`https://www.chiguabaoliao.com/category/${activeSlug}`}
        jsonLd={[categoryJsonLd, breadcrumbJsonLd]}
      />

      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <Breadcrumb
          items={[
            { label: currentCategory?.name || '分类' },
          ]}
        />

        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat.slug}
              onClick={() => setActiveSlug(cat.slug)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                activeSlug === cat.slug
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </span>
            </button>
          ))}
        </div>

        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
          {currentCategory?.name || '分类'}
        </h1>
        <p className="text-foreground-500 text-sm mb-8">{currentCategory?.description}</p>

        {categoryArticles.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {categoryArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {article.isHot && (
                    <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-primary-500 text-white text-xs font-medium">
                      热门
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors mb-2">
                    {article.title}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.publishDate}</span>
                    <span>{(article.readCount / 10000).toFixed(1)}万阅读</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-inbox-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">该分类暂无文章</p>
          </div>
        )}
      </div>
    </>
  );
}